import { useEffect, useMemo, useRef, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Box, Group, Paper, ScrollArea, Text } from "@mantine/core";
import { useChatWindow, usePublicSettings } from "../api/queries";
import type { ChatMessage } from "../api/types";
import { usePlayer } from "../state/player";
import { LIVE_DRIFT_MS, formatTime } from "../time/format";
import { useNow } from "../time/useNow";
import { useZone } from "../time/useZone";
import { gapSeconds, mergeMessages, replayWindow, toLines, visibleAt } from "./replay";
import { useChatStreamStore } from "./stream";

/**
 * How much either side of the current window is fetched, and how coarsely the window moves.
 *
 * The step is what keeps this from issuing a request per frame: playback advances constantly,
 * so the window is anchored to a five-minute grid and only changes when the playhead crosses
 * one. The lead covers the boundary, so a line said just before the step still appears.
 */
const WINDOW_STEP_MS = 5 * 60_000;
const WINDOW_LEAD_MS = 60_000;

/**
 * How coarsely the live/replay test below reads the clock.
 *
 * It is measuring against `LIVE_DRIFT_MS`, two whole minutes, so five seconds of slack on the
 * threshold is invisible -- and it is what stops a paused page sitting in the archive from
 * re-rendering the transcript once a second for a boolean that has not moved.
 */
const LIVE_TICK_MS = 5_000;

export function ChatPanel() {
  const settings = usePublicSettings();
  const zone = useZone();
  const positionMs = usePlayer((s) => s.positionMs);
  const source = usePlayer((s) => s.source);
  const atLive = usePlayer((s) => s.atLive);
  const live = useChatStreamStore((s) => s.live);
  const status = useChatStreamStore((s) => s.status);
  const now = useNow(LIVE_TICK_MS);
  const { t } = useTranslation();

  const enabled = settings.data?.chat_enabled ?? false;
  const offsetSeconds = settings.data?.chat_replay_offset_seconds ?? 0;

  // Live when playing the live stream, or when the playhead is within a couple of minutes of
  // now. Scrubbing back into the archive switches to replay, which is the whole point: chat
  // from last Tuesday belongs with last Tuesday's audio, not with this minute's.
  const isLive =
    source?.kind === "live" ||
    // The timeline's live mode counts on its own. With nothing playing the position never
    // advances, so the clock test below would otherwise flip a page that is sitting at the live
    // edge into replaying the instant it was loaded, two minutes after it was loaded.
    (atLive && source === null) ||
    // Past this far from the live edge the panel is replaying rather than tailing, and the
    // timeline's pill switches from saying LIVE to offering to go there on the same number.
    (positionMs > 0 && now - positionMs < LIVE_DRIFT_MS);

  const window_ = useMemo(
    () =>
      isLive || positionMs <= 0
        ? null
        : replayWindow(positionMs, offsetSeconds, WINDOW_LEAD_MS, WINDOW_STEP_MS),
    [isLive, positionMs, offsetSeconds],
  );

  const fetched = useChatWindow(window_?.startMs ?? null, window_?.endMs ?? null, enabled);

  const lines = useMemo(() => {
    if (!enabled) return [];
    if (isLive) return toLines(mergeMessages([], live));
    const merged = mergeMessages(fetched.data?.messages ?? [], []);
    return toLines(visibleAt(merged, positionMs, offsetSeconds));
  }, [enabled, isLive, live, fetched.data, positionMs, offsetSeconds]);

  const unlogged = gapSeconds(fetched.data?.gaps ?? []);

  if (!enabled) return null;

  return (
    <Paper
      component="section"
      aria-label={t("chatPanel.sectionLabel")}
      // The `Grid.Col` around this is a flex column of definite height, so one `flex: 1`
      // replaces both the phone stack's `flex: 1` and the desktop cell's stretch.
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* `wrap="nowrap"` because Group wraps by default and this row never did: in a narrow
          column the mode and the polling note belong on the label's line, shrinking rather
          than dropping under it. */}
      <Group
        component="header"
        gap={8}
        align="baseline"
        wrap="nowrap"
        px={12}
        py={10}
        style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}
      >
        <Text component="h2" size="xs" fw={600} lts="0.35px" tt="uppercase" c="dimmed">
          {t("chatPanel.heading")}
        </Text>
        <Text component="span" fz="11px" c="accent">
          {isLive ? t("chatPanel.modeLive") : t("chatPanel.modeReplay")}
        </Text>
        {status === "polling" && (
          // Named rather than hidden: a viewer seeing chat arrive in clumps should know it is
          // the transport degrading, not the channel going quiet.
          <Text component="span" ml="auto" fz="11px" c="warn" title={t("chatPanel.pollingTitle")}>
            {t("chatPanel.polling")}
          </Text>
        )}
      </Group>
      <ChatScroller lines={lines} zone={zone.name} />
      {lines.length === 0 && (
        <Text p="12px" fz="12px" c="dimmed">
          {unlogged > 0
            ? t("chatPanel.emptyUnlogged")
            : isLive
              ? t("chatPanel.emptyLive")
              : t("chatPanel.emptyReplay")}
        </Text>
      )}
    </Paper>
  );
}

function ChatScroller({ lines, zone }: { lines: ReturnType<typeof toLines>; zone: string }) {
  // ScrollArea's viewport is the scrolling box now, and it exposes the same three numbers the
  // tail-pin arithmetic below reads off it.
  const ref = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);

  // Follow the tail, but stop following the moment someone scrolls up to read: yanking the
  // view back to the bottom while a person is reading is the single most irritating thing a
  // chat panel can do.
  const onScroll = () => {
    const element = ref.current;
    if (!element) return;
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
    pinnedRef.current = distance < 24;
  };

  useEffect(() => {
    const element = ref.current;
    if (element && pinnedRef.current) element.scrollTop = element.scrollHeight;
  }, [lines]);

  return (
    // The scrolling box -- `flex`, `min-height`, `overflow-y`, `overscroll-behavior` -- is these
    // props, because the box they describe is the viewport now. The list's own box -- margin,
    // padding, list-style, overflow-wrap -- is props on the `<ol>` below.
    <ScrollArea
      viewportRef={ref}
      viewportProps={{ onScroll }}
      overscrollBehavior="contain"
      style={{ flex: 1, minHeight: 0 }}
    >
      <Box
        component="ol"
        m={0}
        px={12}
        py={8}
        // A transcript is read left to right and wraps; long single-word URLs must not force
        // the whole panel to scroll sideways.
        style={{ listStyle: "none", overflowWrap: "anywhere" }}
      >
        {lines.map(({ message, showNick }) => (
          <Line key={message.id} message={message} showNick={showNick} zone={zone} />
        ))}
      </Box>
    </ScrollArea>
  );
}

/** The stamp column and the body column, at the transcript's own size. */
const LINE_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr)",
  gap: "0 8px",
  padding: "1px 0",
  fontSize: 13,
};

function Line({
  message,
  showNick,
  zone,
}: {
  message: ChatMessage;
  showNick: boolean;
  zone: string;
}) {
  const { t } = useTranslation();
  const stamp = formatTime(Date.parse(message.ts), zone);

  if (message.redacted) {
    return (
      <Box component="li" style={LINE_STYLE}>
        <Stamp>{stamp}</Stamp>
        <Body dim redacted>
          {t("chatPanel.redacted")}
        </Body>
      </Box>
    );
  }

  if (message.kind === "action") {
    return (
      <Box component="li" style={LINE_STYLE}>
        <Stamp>{stamp}</Stamp>
        <Body dim>
          <Nick>{message.nick}</Nick> {message.body}
        </Body>
      </Box>
    );
  }

  if (message.kind === "topic") {
    return (
      <Box component="li" style={LINE_STYLE}>
        <Stamp>{stamp}</Stamp>
        {/* The nick keeps its own span rather than riding inside a `<Trans>`: the sentence's
            other slot is a chat body, and `Trans` parses the interpolated string as markup,
            so a line containing a tag would be mounted as one. A plain `t()` is a string, and
            React renders it as text. Each sentence is still one whole key. */}
        <Body dim>
          <Nick>{message.nick}</Nick> {t("chatPanel.setTopic", { topic: message.body })}
        </Body>
      </Box>
    );
  }

  if (message.kind === "kick") {
    return (
      <Box component="li" style={LINE_STYLE}>
        <Stamp>{stamp}</Stamp>
        <Body dim>
          <Nick>{message.nick}</Nick> {t("chatPanel.kicked", { target: message.target })}
          {message.body ? ` (${message.body})` : ""}
        </Body>
      </Box>
    );
  }

  if (message.kind === "mode") {
    return (
      <Box component="li" style={LINE_STYLE}>
        <Stamp>{stamp}</Stamp>
        <Body dim>
          <Nick>{message.nick}</Nick> {t("chatPanel.setMode", { mode: message.body })}
        </Body>
      </Box>
    );
  }

  return (
    <Box component="li" style={LINE_STYLE}>
      <Stamp>{stamp}</Stamp>
      <Body>
        {showNick && (
          <>
            <Nick>{message.nick}</Nick>{" "}
          </>
        )}
        {message.body}
      </Body>
    </Box>
  );
}

/** Tabular so the times line up down the column rather than jittering digit by digit. */
function Stamp({ children }: { children: string }) {
  return (
    <Text
      component="time"
      c="dimmed"
      fz="11px"
      pt="2px"
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      {children}
    </Text>
  );
}

/** `fz="inherit"` keeps the nick at the line's own size: `Text` would otherwise impose its own. */
function Nick({ children }: { children: string }) {
  return (
    <Text component="span" c="accent" fw={600} fz="inherit">
      {children}
    </Text>
  );
}

/**
 * The body column. `fz="inherit"` for the same reason `Nick` uses it -- the line owns the size.
 * `dim` is the wash the action, meta and redacted lines carry.
 */
function Body({
  dim,
  redacted,
  children,
}: {
  dim?: boolean;
  redacted?: boolean;
  children: ReactNode;
}) {
  return (
    <Text
      component="span"
      fz="inherit"
      c={dim ? "dimmed" : undefined}
      fs={dim ? "italic" : undefined}
      opacity={redacted ? 0.7 : undefined}
    >
      {children}
    </Text>
  );
}
